import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import {
  type BuTStatus,
  KPFStandardMembershipFeeOptions,
  LogicExpression,
  StudentType,
} from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import type { FormOfFinancing } from '../../../_cms/financing/entities/FormOfFinancing.js';
import { FormOfFinancingBuT } from '../../../_cms/financing/entities/FormOfFinancingBuT.js';
import { FormOfFinancingMitgliedsbeitrag } from '../../../_cms/financing/entities/FormOfFinancingMitgliedsbeitrag.js';
import { FormOfFinancingStiftungsgeld } from '../../../_cms/financing/entities/FormOfFinancingStiftungsgeld.js';
import { PRODUCT_BLACKLIST, Product } from '../../../_cms/products/entities/Product.js';
import { ProductKGF } from '../../../_cms/products/entities/ProductKGF.js';
import { ProductLKK } from '../../../_cms/products/entities/ProductLKK.js';
import { ProductOGT } from '../../../_cms/products/entities/ProductOGT.js';
import { ProductSCH } from '../../../_cms/products/entities/ProductSCH.js';
import { AcquisitionRecord } from '../../../AcquisitionRecord.js';
import { RAGFundingInformation } from '../../../funding/entities/RAGFundingInformation.js';
import { kpfTokenChangeInformation, kpfTokenCreationInformation } from '../../../mailer/mailTemplates.js';
import { chancenWerkMailer } from '../../../mailer/server/mailer.js';
import { Role } from '../../../semantics/Role.js';
import { SemanticRelationship } from '../../../semantics/SemanticRelationship.js';
import { PARTNER_BLACKLIST, Partner } from '../../partner/entities/Partner.js';
import { PERSON_BLACKLIST } from '../../users/entities/ChancenwerkUser.js';
import { ExternalContactPerson } from '../../users/entities/ExternalContactPerson.js';
import { Mitarbeiter } from '../../users/entities/Mitarbeiter.js';
import { Student } from '../../users/entities/Student.js';
import { resolveKPFRelationshipsForRole } from '../api/resolveKPFRelationshipsForRole.js';
import {
  FormOfFinancingLogicExpression,
  FormOfFinancingLogicExpressionArrayUnion,
} from './FormOfFinancingLogicExpression.js';

export const KPF_BLACKLIST = {
  _class: 1,
  token: 1,
  schuKos: PERSON_BLACKLIST,
  leKos: PERSON_BLACKLIST,
  ansprechPersonExtern: PERSON_BLACKLIST,
  butAnsprechPersonExtern: PERSON_BLACKLIST,
  personsStrategisch: PERSON_BLACKLIST,
  personsOperativ: PERSON_BLACKLIST,
  personsVerwaltung: PERSON_BLACKLIST,
  personsBUTOperativ: PERSON_BLACKLIST,
  formOfFinanzingLogicString: 1,
  school: PARTNER_BLACKLIST,
  usStudents: 1,
  osStudents: 1,
  schoolID: 1,
  substituteKPFIDs: 1,
  substituteKPFs: 1,
  tokenAccessSoftLimitMailSent: 1,
  tokenAccessHardLimitMailSent: 1,
  generateLevumiTestReminderForSchuKos: 1,
  attendanceLogsCount: 1,
  ragPartnerInformation: 1,
  acquisitionRecord: 1,
  produkt: PRODUCT_BLACKLIST,
  schoolSupport: PERSON_BLACKLIST,
};

@Entity()
export class KPF<T extends Product = Product> extends MongoEntity {
  public static override _class = 'KPF';
  public static override _collectionName = 'kpfs';

  public static override get allFields(): BaseQLSelectionSet<KPF> {
    return selectionSet(() => KPF, 3);
  }

  public get stringRepresentation(): string {
    return `${this.kuerzel} ${this.school?.name ? `(${this.school.name})` : ''}`;
  }

  @Field(() => ID)
  public schoolID?: string;

  @DataLoadedField(() => Partner, ({ schoolID }) => schoolID)
  public school: Maybe<Partner>;

  @Field(() => Product)
  public produkt: Maybe<T>;

  @Field(() => String)
  public butStatus: Maybe<BuTStatus>;

  @Field(() => String)
  public kostenstellennummer?: string;

  public get kuerzel() {
    return (this.school?.kuerzel ?? 'X_X') + '_' + (this.produkt?.kuerzel ?? 'X');
  }

  @Field(() => String)
  public kommentar?: string;

  @Field(() => String)
  public token?: string;

  @validate<KPF<T>>(({ value, key, target }) => {
    if (value && target.endDatum && value > target.endDatum)
      throw new ValidationError(`Das Startdatum muss vor dem Enddatum liegen.`, { key, target });
  })
  @Field(() => DateTime)
  public startDatum: Maybe<DateTime>;

  @validate<KPF<T>>(({ value, key, target }) => {
    if (value && target.startDatum && value < target.startDatum)
      throw new ValidationError(`Das Enddatum muss hinter dem Startdatum liegen.`, { key, target });
  })
  @Field(() => DateTime)
  public endDatum: Maybe<DateTime>;

  @Field(() => AcquisitionRecord, {
    resolve: function (this: KPF<T>) {
      return async () => {
        const res = (await loadMongoData(AcquisitionRecord, { kpfID: this._id })).at(0);
        return res as AcquisitionRecord;
      };
    },
  })
  public acquisitionRecord: Maybe<AcquisitionRecord>;

  @Field(() => ExternalContactPerson, {
    resolve: function (this: KPF<T>) {
      return async () =>
        (await resolveKPFRelationshipsForRole<ExternalContactPerson, T>(this, Role.EXTERNAL_CONTACT)).at(0);
    },
  })
  public ansprechPersonExtern?: ExternalContactPerson;

  @Field(() => ExternalContactPerson, {
    resolve: function (this: KPF<T>) {
      return async () =>
        (await resolveKPFRelationshipsForRole<ExternalContactPerson, T>(this, Role.EXTERNAL_CONTACT_BUT)).at(0);
    },
  })
  public butAnsprechPersonExtern?: ExternalContactPerson;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_STRATEGY);
    },
  })
  public personsStrategisch: Maybe<Mitarbeiter[]>;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_OPERATIV);
    },
  })
  public personsOperativ: Maybe<Mitarbeiter[]>;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_ADMINISTRATION);
    },
  })
  public personsVerwaltung: Maybe<Mitarbeiter[]>;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_BUT_OPERATIV);
    },
  })
  public personsBUTOperativ: Maybe<Mitarbeiter[]>;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.SCHOOL_SUPPORT);
    },
  })
  public schoolSupport: Maybe<Mitarbeiter[]>;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.SCHUKO);
    },
  })
  public schuKos: Maybe<Mitarbeiter[]>;

  @Field(() => [String], { default: v => v ?? [] })
  public substituteKPFIDs: Maybe<string[]>;

  @DataLoadedField(() => [KPF], ({ substituteKPFIDs }) => substituteKPFIDs ?? [])
  public substituteKPFs: Maybe<Array<KPF<T>>>;

  @Field(() => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.LEKO);
    },
  })
  public leKos: Maybe<Mitarbeiter[]>;

  @Field(() => [Student], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Student, T>(this, Role.STUDENT);
    },
  })
  public usStudents: Maybe<Student[]>;

  @Field(() => [Student], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Student, T>(this, Role.STUDENT, StudentType.OS);
    },
  })
  public osStudents: Maybe<Student[]>;

  @Field(() => Boolean, { default: v => v ?? false })
  public tokenAccessForExternalsEnabled?: boolean;

  @Field(() => String)
  public accessTokenForExternals?: string;

  @Field(() => Boolean, { default: v => v ?? false })
  public tokenAccessEnabled?: boolean;

  @Field(() => String)
  public accessTokenForStudents?: string;

  @Field(() => [String])
  public studentCreationMailOverride: Maybe<string[]>;

  @Field(() => Int, { default: v => v ?? 0 })
  public tokenAccessCount?: number;

  @Field(() => Int, { default: v => v ?? 0 })
  public tokenAccessCountMax?: number;

  @Field(() => Boolean, { default: v => v ?? false })
  public tokenAccessSoftLimitMailSent?: boolean;

  @Field(() => Boolean, { default: v => v ?? false })
  public tokenAccessHardLimitMailSent?: boolean;

  @Field(() => String)
  public email: Maybe<string>;

  @Field(() => Boolean)
  public generateLevumiTestReminderForSchuKos: Maybe<boolean>;

  @Field(() => String)
  public osCoachMail: Maybe<string>;

  @Field(() => String)
  public schukoTel: Maybe<string>;

  @Field(() => String)
  public osCoachTel: Maybe<string>;

  @Field(() => [RAGFundingInformation], { default: v => v ?? [] })
  public ragPartnerInformation: Maybe<RAGFundingInformation[]>;

  // Defines the logicstring which in turn builds the register form. Do we ask for a BuT key or an iban for example
  @Field(() => FormOfFinancingLogicExpressionArrayUnion, {
    default: v =>
      v ??
      new FormOfFinancingLogicExpression({
        type: LogicExpression.OR,
        formsOfFinancingArray: [
          new FormOfFinancingLogicExpression({
            type: LogicExpression.AND,
            formsOfFinancingArray: [new FormOfFinancingMitgliedsbeitrag({}), new FormOfFinancingStiftungsgeld({})],
          }),
          new FormOfFinancingBuT({}),
        ],
      }),
  })
  public formOfFinanzingLogicString: Maybe<FormOfFinancingLogicExpression | FormOfFinancing>;

  // Predefined membership fee. Determines the preselected value in the registration form
  @validate<KPF<T>>(({ value, key, target }) => {
    //! 0 is a valid value
    if (value !== null && value !== undefined && !KPFStandardMembershipFeeOptions.includes(value)) {
      throw new ValidationError(`${value} ist kein gültiger Standardmitgliedsbeitrag.`, { key, target });
    }
  })
  @Field(() => Int, { default: v => v ?? 20 })
  public standardMembershipFee: Maybe<number>;

  @Field(() => Int)
  public attendanceLogsCount?: number;

  private static async updatePartnerChangeDate(partnerID: Maybe<string>) {
    if (!partnerID) {
      logger.warn('Couldnt update Partner with id: ' + partnerID);
      return;
    }
    const partnerCollection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
    const partnerToUpdate = await partnerCollection.findOne({ _id: partnerID });

    const partnerConstructed = constructValue(partnerToUpdate) as Partner;
    await partnerConstructed.save();
  }

  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: KPF<Product>,
  ): Promise<void> {
    // 1 Check duplicate kpf assignment
    await this.checkDuplicateKpfCombination(update);

    // 2 Check duplicate access token to deconflict future users
    await this._checkDuplicateToken(update);

    await this.checkDurchfuehrungsTage(update);
  }

  /**
   * Durchführungstage drive the Lernförderung courses, so they have to be complete and unique
   * before they hit the database. This runs on create *and* update - an incomplete day slipping in
   * on create used to produce a course without weekday and times, which never showed up in the
   * CHANCENkräfte overview.
   */
  private static async checkDurchfuehrungsTage(kpf: KPF<Product>) {
    if (![ProductLKK._class, ProductOGT._class, ProductKGF._class].includes(kpf.produkt?._class ?? '')) return;

    const { checkDuplicateDurchfuehrungsTage, validateDurchfuehrungsTage } = await import(
      '../../../courses/CourseLernfoerderung.js'
    );
    const days = (kpf.produkt as ProductLKK | ProductOGT | ProductKGF).durchfuehrungsTage ?? [];
    validateDurchfuehrungsTage(days);
    checkDuplicateDurchfuehrungsTage(days);
  }

  private static async _checkDuplicateToken(kpf: KPF) {
    if (!A.isServer) throw new ValidationError('This is a serverside helper only');
    const kpfCollection = await getCollection(KPF._class);
    let count = 0;
    if (kpf.has('accessTokenForExternals') && kpf.accessTokenForExternals) {
      count = await kpfCollection.countDocuments({
        _id: { $ne: kpf._id },
        $or: [
          { accessTokenForExternals: kpf.accessTokenForExternals },
          { accessTokenForStudents: kpf.accessTokenForExternals },
        ],
      });
      if (count >= 1) throw new ValidationError(`Der Token für externe Lehrkräfte ist bereits vergeben.`);
    }
    if (kpf.has('accessTokenForStudents') && kpf.accessTokenForStudents) {
      count = await kpfCollection.countDocuments({
        _id: { $ne: kpf._id },
        $or: [
          { accessTokenForExternals: kpf.accessTokenForStudents },
          { accessTokenForStudents: kpf.accessTokenForStudents },
        ],
      });
      if (count >= 1) throw new ValidationError(`Der Token für Schüler:innen ist bereits vergeben.`);
    }
    if (
      kpf.has('accessTokenForExternals') &&
      kpf.has('accessTokenForStudents') &&
      kpf.accessTokenForStudents &&
      kpf.accessTokenForExternals
    ) {
      count = await kpfCollection.countDocuments({
        _id: kpf._id,
        $or: [
          { accessTokenForExternals: kpf.accessTokenForStudents },
          { accessTokenForStudents: kpf.accessTokenForExternals },
        ],
      });
      if (count >= 1) throw new ValidationError(`Die Token innerhalb eines KPFs müssen unterschiedlich sein.`);
    }
    return true;
  }

  private static async _sendTokenUpdateInfo(newEntity: KPF<Product>, oldEntity?: PartialEntityData<KPF<Product>>) {
    try {
      const semanticCollection = await getRawCollection<EntityData<SemanticRelationship>>(
        SemanticRelationship._collectionName,
      );

      // TODO do we have more than one person here? Technically we could do it
      const externalRelation = (await semanticCollection.find({ predicateID: Role.EXTERNAL_CONTACT }).toArray()).at(0);
      if (!externalRelation?.subjectID) return;

      if (newEntity.accessTokenForStudents && !oldEntity?.accessTokenForStudents) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Schüler:innen wurde angelegt',
            html: kpfTokenCreationInformation({
              tokenSection: 'Schüler:innen',
              token: newEntity.accessTokenForStudents ?? 'Kein Token gefunden',
              usage: newEntity.tokenAccessCountMax?.toString() ?? '',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      } else if (
        newEntity.accessTokenForStudents &&
        oldEntity?.accessTokenForStudents &&
        oldEntity.accessTokenForStudents !== newEntity.accessTokenForStudents
      ) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Schüler:innen wurde geändert',
            html: kpfTokenChangeInformation({
              tokenSection: 'Schüler:innen',
              token: newEntity.accessTokenForStudents ?? 'Kein Token gefunden',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      }

      // Send Mail if token has changed
      if (newEntity.accessTokenForExternals && !oldEntity?.accessTokenForExternals) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Lehrkräfte wurde angelegt',
            html: kpfTokenCreationInformation({
              tokenSection: 'Lehrkräfte',
              token: newEntity.accessTokenForExternals ?? 'Kein Token gefunden',
              usage: '',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      } else if (
        newEntity.accessTokenForExternals &&
        oldEntity?.accessTokenForExternals &&
        oldEntity.accessTokenForExternals !== newEntity.accessTokenForExternals
      ) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Lehrkräfte hat sich geändert',
            html: kpfTokenChangeInformation({
              tokenSection: 'Lehrkräfte',
              token: newEntity.accessTokenForExternals ?? 'Kein Token gefunden',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  static override async beforeCreate(entity: KPF<Product>): Promise<void> {
    await super.beforeCreate(entity);
    await this.checkDuplicateKpfCombination(entity);
    await this.checkDurchfuehrungsTage(entity);
  }

  static override async afterCreate(entity: KPF<Product>, result: InsertOneResult<Document>): Promise<void> {
    // create courses with data from duerchführungstage and locations. If there isnt any data we create an empty course
    try {
      // if (entity.produkt?._class === ProductLKK._class) {
      if ([ProductLKK._class, ProductOGT._class, ProductKGF._class].includes(entity.produkt?._class ?? '')) {
        const { synchronizeKPFCoursesWithDB } = await import('../../../courses/CourseLernfoerderung.js');
        await synchronizeKPFCoursesWithDB(entity as unknown as KPF<ProductLKK | ProductOGT | ProductKGF>);
      } else if (entity.produkt?._class === ProductSCH._class) {
        const SCH = await import('../../../courses/CourseChancenschule.js');
        await SCH.CourseChancenschule.updateAndCreateCoursesOfKPF(entity as unknown as KPF<ProductSCH>);
      }
    } catch (e) {
      logger.error(e);
    }

    // sync the change to the partner so it can keep track of changes too
    await this.updatePartnerChangeDate(entity.schoolID);

    // Send Mail if token has changed
    await this._sendTokenUpdateInfo(entity);

    return super.afterCreate(entity, result);
  }

  static override async afterUpdate(
    oldEntity: PartialEntityData<KPF<Product>>,
    newEntity: KPF<Product>,
    result: UpdateResult<Document>,
  ): Promise<void> {
    try {
      // create courses with data from duerchführungstage and locations. If there isnt any data we create an empty course

      if ([ProductLKK._class, ProductOGT._class, ProductKGF._class].includes(newEntity.produkt?._class ?? '')) {
        const { synchronizeKPFCoursesWithDB } = await import('../../../courses/CourseLernfoerderung.js');
        const oldKpf = constructValue(oldEntity) as KPF<ProductLKK | ProductOGT | ProductKGF>;
        await synchronizeKPFCoursesWithDB(newEntity as unknown as KPF<ProductLKK | ProductOGT | ProductKGF>, oldKpf);
      } else if (newEntity.produkt?._class === ProductSCH._class) {
        const SCH = await import('../../../courses/CourseChancenschule.js');
        const oldKpf = constructValue(oldEntity) as KPF<ProductSCH>;
        await SCH.CourseChancenschule.updateAndCreateCoursesOfKPF(newEntity as unknown as KPF<ProductSCH>, oldKpf);
      }
    } catch (e) {
      logger.error(e);
    }

    // sync the change to the partner so it can keep track of changes too
    await this.updatePartnerChangeDate(newEntity.schoolID);

    // Send Mail if token has changed
    await this._sendTokenUpdateInfo(newEntity, oldEntity);

    return super.afterUpdate(oldEntity, newEntity, result);
  }

  private static async checkDuplicateKpfCombination(kpf: KPF<Product>) {
    if (!A.isServer) throw new ValidationError('This is a serverside helper only');

    const kpfCollection = await getRawCollection<EntityData<KPF>>(KPF._collectionName);
    const count = await kpfCollection.countDocuments({
      _id: { $ne: kpf._id },
      schoolID: kpf.schoolID,
      'produkt._class': kpf.produkt?._class,
    });
    if (count >= 1) throw new ValidationError(`Diese Produkt-Schule-Kombination gibt es bereits.`);
    return true;
  }

  static override async afterRemove(id: string, entityData: any): Promise<void> {
    await super.afterRemove(id, entityData);
    const relationshipCollection = await getRawCollection(SemanticRelationship._collectionName);
    await relationshipCollection.deleteMany({ $or: [{ objectID: id }, { subjectID: id }] });
  }

  override toString() {
    return [this.kuerzel, this.school?.name].filter(v => v).join(' ');
  }
}
